import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import { transactionLineItems, transitionPrivileged, post } from '../../util/api';
import * as log from '../../util/log';
import {
  updatedEntities,
  denormalisedEntities,
  denormalisedResponseEntities,
} from '../../util/data';
import {
  resolveLatestProcessName,
  getProcess,
  isBookingProcess,
} from '../../transactions/transaction';


import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserNotifications, fetchCurrentUser } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/TransactionPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/TransactionPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/TransactionPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/TransactionPage/FETCH_TRANSACTION_ERROR';

export const FETCH_TRANSITIONS_REQUEST = 'app/TransactionPage/FETCH_TRANSITIONS_REQUEST';
export const FETCH_TRANSITIONS_SUCCESS = 'app/TransactionPage/FETCH_TRANSITIONS_SUCCESS';
export const FETCH_TRANSITIONS_ERROR = 'app/TransactionPage/FETCH_TRANSITIONS_ERROR';

export const TRANSITION_REQUEST = 'app/TransactionPage/MARK_RECEIVED_REQUEST';
export const TRANSITION_SUCCESS = 'app/TransactionPage/TRANSITION_SUCCESS';
export const TRANSITION_ERROR = 'app/TransactionPage/TRANSITION_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/TransactionPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/TransactionPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/TransactionPage/FETCH_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/TransactionPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/TransactionPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/TransactionPage/SEND_MESSAGE_ERROR';

export const SEND_REVIEW_REQUEST = 'app/TransactionPage/SEND_REVIEW_REQUEST';
export const SEND_REVIEW_SUCCESS = 'app/TransactionPage/SEND_REVIEW_SUCCESS';
export const SEND_REVIEW_ERROR = 'app/TransactionPage/SEND_REVIEW_ERROR';

export const FETCH_MONTHLY_TIME_SLOTS_REQUEST =
  'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_REQUEST';
export const FETCH_MONTHLY_TIME_SLOTS_SUCCESS =
  'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_SUCCESS';
export const FETCH_MONTHLY_TIME_SLOTS_ERROR = 'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_ERROR';

export const FETCH_TIME_SLOTS_FOR_DATE_REQUEST =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_REQUEST';
export const FETCH_TIME_SLOTS_FOR_DATE_SUCCESS =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_SUCCESS';
export const FETCH_TIME_SLOTS_FOR_DATE_ERROR =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/TransactionPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/TransactionPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/TransactionPage/FETCH_LINE_ITEMS_ERROR';

export const CANCEL_SALE_REQUEST = 'app/TransactionPage/CANCEL_SALE_REQUEST';
export const CANCEL_SALE_SUCCESS = 'app/TransactionPage/CANCEL_SALE_SUCCESS';
export const CANCEL_SALE_ERROR = 'app/TransactionPage/CANCEL_SALE_ERROR';

export const CAPTURE_DEPOSIT_REQUEST = 'app/TransactionPage/CAPTURE_DEPOSIT_REQUEST';
export const CAPTURE_DEPOSIT_SUCCESS = 'app/TransactionPage/CAPTURE_DEPOSIT_SUCCESS';
export const CAPTURE_DEPOSIT_ERROR = 'app/TransactionPage/CAPTURE_DEPOSIT_ERROR';

export const TRIGGER_DEPOSIT_CHARGE_REQUEST = 'app/TransactionPage/TRIGGER_DEPOSIT_CHARGE_REQUEST';
export const TRIGGER_DEPOSIT_CHARGE_SUCCESS = 'app/TransactionPage/TRIGGER_DEPOSIT_CHARGE_SUCCESS';
export const TRIGGER_DEPOSIT_CHARGE_ERROR = 'app/TransactionPage/TRIGGER_DEPOSIT_CHARGE_ERROR';

export const REQUEST_PICKUP_TIME_CHANGE = 'app/TransactionPage/REQUEST_PICKUP_TIME_CHANGE';
export const REQUEST_PICKUP_TIME_CHANGE_SUCCESS = 'app/TransactionPage/REQUEST_PICKUP_TIME_CHANGE_SUCCESS';
export const REQUEST_PICKUP_TIME_CHANGE_ERROR = 'app/TransactionPage/REQUEST_PICKUP_TIME_CHANGE_ERROR';

export const ACCEPT_PICKUP_TIME_CHANGE = 'app/TransactionPage/ACCEPT_PICKUP_TIME_CHANGE';
export const ACCEPT_PICKUP_TIME_CHANGE_SUCCESS = 'app/TransactionPage/ACCEPT_PICKUP_TIME_CHANGE_SUCCESS';
export const ACCEPT_PICKUP_TIME_CHANGE_ERROR = 'app/TransactionPage/ACCEPT_PICKUP_TIME_CHANGE_ERROR';

export const DECLINE_PICKUP_TIME_CHANGE = 'app/TransactionPage/DECLINE_PICKUP_TIME_CHANGE';
export const DECLINE_PICKUP_TIME_CHANGE_SUCCESS = 'app/TransactionPage/DECLINE_PICKUP_TIME_CHANGE_SUCCESS';
export const DECLINE_PICKUP_TIME_CHANGE_ERROR = 'app/TransactionPage/DECLINE_PICKUP_TIME_CHANGE_ERROR';

export const REQUEST_EXTENSION = 'app/TransactionPage/REQUEST_EXTENSION';
export const REQUEST_EXTENSION_SUCCESS = 'app/TransactionPage/REQUEST_EXTENSION_SUCCESS';
export const REQUEST_EXTENSION_ERROR = 'app/TransactionPage/REQUEST_EXTENSION_ERROR';

export const ACCEPT_EXTENSION = 'app/TransactionPage/ACCEPT_EXTENSION';
export const ACCEPT_EXTENSION_SUCCESS = 'app/TransactionPage/ACCEPT_EXTENSION_SUCCESS';
export const ACCEPT_EXTENSION_ERROR = 'app/TransactionPage/ACCEPT_EXTENSION_ERROR';

export const DECLINE_EXTENSION = 'app/TransactionPage/DECLINE_EXTENSION';
export const DECLINE_EXTENSION_SUCCESS = 'app/TransactionPage/DECLINE_EXTENSION_SUCCESS';
export const DECLINE_EXTENSION_ERROR = 'app/TransactionPage/DECLINE_EXTENSION_ERROR';

export const REQUEST_EARLY_RETURN = 'app/TransactionPage/REQUEST_EARLY_RETURN';
export const REQUEST_EARLY_RETURN_SUCCESS = 'app/TransactionPage/REQUEST_EARLY_RETURN_SUCCESS';
export const REQUEST_EARLY_RETURN_ERROR = 'app/TransactionPage/REQUEST_EARLY_RETURN_ERROR';

export const ACCEPT_EARLY_RETURN = 'app/TransactionPage/ACCEPT_EARLY_RETURN';
export const ACCEPT_EARLY_RETURN_SUCCESS = 'app/TransactionPage/ACCEPT_EARLY_RETURN_SUCCESS';
export const ACCEPT_EARLY_RETURN_ERROR = 'app/TransactionPage/ACCEPT_EARLY_RETURN_ERROR';

export const DECLINE_EARLY_RETURN = 'app/TransactionPage/DECLINE_EARLY_RETURN';
export const DECLINE_EARLY_RETURN_SUCCESS = 'app/TransactionPage/DECLINE_EARLY_RETURN_SUCCESS';
export const DECLINE_EARLY_RETURN_ERROR = 'app/TransactionPage/DECLINE_EARLY_RETURN_ERROR';

export const FILE_DAMAGE_CLAIM = 'app/TransactionPage/FILE_DAMAGE_CLAIM';
export const FILE_DAMAGE_CLAIM_SUCCESS = 'app/TransactionPage/FILE_DAMAGE_CLAIM_SUCCESS';
export const FILE_DAMAGE_CLAIM_ERROR = 'app/TransactionPage/FILE_DAMAGE_CLAIM_ERROR';

export const AGREE_DAMAGE_CLAIM = 'app/TransactionPage/AGREE_DAMAGE_CLAIM';
export const AGREE_DAMAGE_CLAIM_SUCCESS = 'app/TransactionPage/AGREE_DAMAGE_CLAIM_SUCCESS';
export const AGREE_DAMAGE_CLAIM_ERROR = 'app/TransactionPage/AGREE_DAMAGE_CLAIM_ERROR';

export const DISPUTE_DAMAGE_CLAIM = 'app/TransactionPage/DISPUTE_DAMAGE_CLAIM';
export const DISPUTE_DAMAGE_CLAIM_SUCCESS = 'app/TransactionPage/DISPUTE_DAMAGE_CLAIM_SUCCESS';
export const DISPUTE_DAMAGE_CLAIM_ERROR = 'app/TransactionPage/DISPUTE_DAMAGE_CLAIM_ERROR';

export const RESOLVE_DAMAGE_CLAIM_FOR_OWNER = 'app/TransactionPage/RESOLVE_DAMAGE_CLAIM_FOR_OWNER';
export const RESOLVE_DAMAGE_CLAIM_FOR_OWNER_SUCCESS = 'app/TransactionPage/RESOLVE_DAMAGE_CLAIM_FOR_OWNER_SUCCESS';
export const RESOLVE_DAMAGE_CLAIM_FOR_OWNER_ERROR = 'app/TransactionPage/RESOLVE_DAMAGE_CLAIM_FOR_OWNER_ERROR';

export const RESOLVE_DAMAGE_CLAIM_FOR_RENTER = 'app/TransactionPage/RESOLVE_DAMAGE_CLAIM_FOR_RENTER';
export const RESOLVE_DAMAGE_CLAIM_FOR_RENTER_SUCCESS = 'app/TransactionPage/RESOLVE_DAMAGE_CLAIM_FOR_RENTER_SUCCESS';
export const RESOLVE_DAMAGE_CLAIM_FOR_RENTER_ERROR = 'app/TransactionPage/RESOLVE_DAMAGE_CLAIM_FOR_RENTER_ERROR';

export const RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS = 'app/TransactionPage/RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS';
export const RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS_SUCCESS = 'app/TransactionPage/RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS_SUCCESS';
export const RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS_ERROR = 'app/TransactionPage/RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transitionInProgress: null,
  transitionError: null,
  cancelInProgress: false,
  cancelSaleError: null,
  captureDepositInProgress: false,
  captureDepositError: null,
  triggerDepositChargeInProgress: false,
  triggerDepositChargeError: null,
  requestTimeChangeInProgress: false,
  requestTimeChangeError: null,
  acceptTimeChangeInProgress: false,
  acceptTimeChangeError: null,
  declineTimeChangeInProgress: false,
  declineTimeChangeError: null,
  requestExtensionInProgress: false,
  requestExtensionError: null,
  acceptExtensionInProgress: false,
  acceptExtensionError: null,
  declineExtensionInProgress: false,
  declineExtensionError: null,
  requestEarlyReturnInProgress: false,
  requestEarlyReturnError: null,
  acceptEarlyReturnInProgress: false,
  acceptEarlyReturnError: null,
  declineEarlyReturnInProgress: false,
  declineEarlyReturnError: null,
  fileDamageClaimInProgress: false,
  fileDamageClaimError: null,
  agreeDamageClaimInProgress: false,
  agreeDamageClaimError: null,
  disputeDamageClaimInProgress: false,
  disputeDamageClaimError: null,
  resolveDamageClaimForOwnerInProgress: false,
  resolveDamageClaimForOwnerError: null,
  resolveDamageClaimForRenterInProgress: false,
  resolveDamageClaimForRenterError: null,
  resolveDamageClaimPlatformAbsorbsInProgress: false,
  resolveDamageClaimPlatformAbsorbsError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

export default function transactionPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case FETCH_TRANSITIONS_REQUEST:
      return { ...state, fetchTransitionsInProgress: true, fetchTransitionsError: null };
    case FETCH_TRANSITIONS_SUCCESS:
      return { ...state, fetchTransitionsInProgress: false, processTransitions: payload };
    case FETCH_TRANSITIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransitionsInProgress: false, fetchTransitionsError: payload };

    case TRANSITION_REQUEST:
      return {
        ...state,
        transitionInProgress: payload,
        transitionError: null,
      };
    case TRANSITION_SUCCESS:
      return { ...state, transitionInProgress: null };
    case TRANSITION_ERROR:
      return {
        ...state,
        transitionInProgress: null,
        transitionError: payload,
      };

    case CANCEL_SALE_REQUEST:
      return { ...state, cancelInProgress: true, cancelSaleError: null };
    case CANCEL_SALE_SUCCESS:
      return { ...state, cancelInProgress: false };
    case CANCEL_SALE_ERROR:
      return { ...state, cancelInProgress: false, cancelSaleError: payload };

    case CAPTURE_DEPOSIT_REQUEST:
      return { ...state, captureDepositInProgress: true, captureDepositError: null };
    case CAPTURE_DEPOSIT_SUCCESS:
      return { ...state, captureDepositInProgress: false };
    case CAPTURE_DEPOSIT_ERROR:
      return { ...state, captureDepositInProgress: false, captureDepositError: payload };

    case TRIGGER_DEPOSIT_CHARGE_REQUEST:
      return { ...state, triggerDepositChargeInProgress: true, triggerDepositChargeError: null };
    case TRIGGER_DEPOSIT_CHARGE_SUCCESS:
      return { ...state, triggerDepositChargeInProgress: false };
    case TRIGGER_DEPOSIT_CHARGE_ERROR:
      return { ...state, triggerDepositChargeInProgress: false, triggerDepositChargeError: payload };

    case REQUEST_PICKUP_TIME_CHANGE:
      return { ...state, requestTimeChangeInProgress: true, requestTimeChangeError: null };
    case REQUEST_PICKUP_TIME_CHANGE_SUCCESS:
      return { ...state, requestTimeChangeInProgress: false };
    case REQUEST_PICKUP_TIME_CHANGE_ERROR:
      return { ...state, requestTimeChangeInProgress: false, requestTimeChangeError: payload };

    case ACCEPT_PICKUP_TIME_CHANGE:
      return { ...state, acceptTimeChangeInProgress: true, acceptTimeChangeError: null };
    case ACCEPT_PICKUP_TIME_CHANGE_SUCCESS:
      return { ...state, acceptTimeChangeInProgress: false };
    case ACCEPT_PICKUP_TIME_CHANGE_ERROR:
      return { ...state, acceptTimeChangeInProgress: false, acceptTimeChangeError: payload };

    case DECLINE_PICKUP_TIME_CHANGE:
      return { ...state, declineTimeChangeInProgress: true, declineTimeChangeError: null };
    case DECLINE_PICKUP_TIME_CHANGE_SUCCESS:
      return { ...state, declineTimeChangeInProgress: false };
    case DECLINE_PICKUP_TIME_CHANGE_ERROR:
      return { ...state, declineTimeChangeInProgress: false, declineTimeChangeError: payload };

    case REQUEST_EXTENSION:
      return { ...state, requestExtensionInProgress: true, requestExtensionError: null };
    case REQUEST_EXTENSION_SUCCESS:
      return { ...state, requestExtensionInProgress: false };
    case REQUEST_EXTENSION_ERROR:
      return { ...state, requestExtensionInProgress: false, requestExtensionError: payload };

    case ACCEPT_EXTENSION:
      return { ...state, acceptExtensionInProgress: true, acceptExtensionError: null };
    case ACCEPT_EXTENSION_SUCCESS:
      return { ...state, acceptExtensionInProgress: false };
    case ACCEPT_EXTENSION_ERROR:
      return { ...state, acceptExtensionInProgress: false, acceptExtensionError: payload };

    case DECLINE_EXTENSION:
      return { ...state, declineExtensionInProgress: true, declineExtensionError: null };
    case DECLINE_EXTENSION_SUCCESS:
      return { ...state, declineExtensionInProgress: false };
    case DECLINE_EXTENSION_ERROR:
      return { ...state, declineExtensionInProgress: false, declineExtensionError: payload };

    case REQUEST_EARLY_RETURN:
      return { ...state, requestEarlyReturnInProgress: true, requestEarlyReturnError: null };
    case REQUEST_EARLY_RETURN_SUCCESS:
      return { ...state, requestEarlyReturnInProgress: false };
    case REQUEST_EARLY_RETURN_ERROR:
      return { ...state, requestEarlyReturnInProgress: false, requestEarlyReturnError: payload };

    case ACCEPT_EARLY_RETURN:
      return { ...state, acceptEarlyReturnInProgress: true, acceptEarlyReturnError: null };
    case ACCEPT_EARLY_RETURN_SUCCESS:
      return { ...state, acceptEarlyReturnInProgress: false };
    case ACCEPT_EARLY_RETURN_ERROR:
      return { ...state, acceptEarlyReturnInProgress: false, acceptEarlyReturnError: payload };

    case DECLINE_EARLY_RETURN:
      return { ...state, declineEarlyReturnInProgress: true, declineEarlyReturnError: null };
    case DECLINE_EARLY_RETURN_SUCCESS:
      return { ...state, declineEarlyReturnInProgress: false };
    case DECLINE_EARLY_RETURN_ERROR:
      return { ...state, declineEarlyReturnInProgress: false, declineEarlyReturnError: payload };

    case FILE_DAMAGE_CLAIM:
      return { ...state, fileDamageClaimInProgress: true, fileDamageClaimError: null };
    case FILE_DAMAGE_CLAIM_SUCCESS:
      return { ...state, fileDamageClaimInProgress: false };
    case FILE_DAMAGE_CLAIM_ERROR:
      return { ...state, fileDamageClaimInProgress: false, fileDamageClaimError: payload };

    case AGREE_DAMAGE_CLAIM:
      return { ...state, agreeDamageClaimInProgress: true, agreeDamageClaimError: null };
    case AGREE_DAMAGE_CLAIM_SUCCESS:
      return { ...state, agreeDamageClaimInProgress: false };
    case AGREE_DAMAGE_CLAIM_ERROR:
      return { ...state, agreeDamageClaimInProgress: false, agreeDamageClaimError: payload };

    case DISPUTE_DAMAGE_CLAIM:
      return { ...state, disputeDamageClaimInProgress: true, disputeDamageClaimError: null };
    case DISPUTE_DAMAGE_CLAIM_SUCCESS:
      return { ...state, disputeDamageClaimInProgress: false };
    case DISPUTE_DAMAGE_CLAIM_ERROR:
      return { ...state, disputeDamageClaimInProgress: false, disputeDamageClaimError: payload };

    case RESOLVE_DAMAGE_CLAIM_FOR_OWNER:
      return { ...state, resolveDamageClaimForOwnerInProgress: true, resolveDamageClaimForOwnerError: null };
    case RESOLVE_DAMAGE_CLAIM_FOR_OWNER_SUCCESS:
      return { ...state, resolveDamageClaimForOwnerInProgress: false };
    case RESOLVE_DAMAGE_CLAIM_FOR_OWNER_ERROR:
      return { ...state, resolveDamageClaimForOwnerInProgress: false, resolveDamageClaimForOwnerError: payload };

    case RESOLVE_DAMAGE_CLAIM_FOR_RENTER:
      return { ...state, resolveDamageClaimForRenterInProgress: true, resolveDamageClaimForRenterError: null };
    case RESOLVE_DAMAGE_CLAIM_FOR_RENTER_SUCCESS:
      return { ...state, resolveDamageClaimForRenterInProgress: false };
    case RESOLVE_DAMAGE_CLAIM_FOR_RENTER_ERROR:
      return { ...state, resolveDamageClaimForRenterInProgress: false, resolveDamageClaimForRenterError: payload };

    case RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS:
      return { ...state, resolveDamageClaimPlatformAbsorbsInProgress: true, resolveDamageClaimPlatformAbsorbsError: null };
    case RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS_SUCCESS:
      return { ...state, resolveDamageClaimPlatformAbsorbsInProgress: false };
    case RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS_ERROR:
      return { ...state, resolveDamageClaimPlatformAbsorbsInProgress: false, resolveDamageClaimPlatformAbsorbsError: payload };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
        initialMessageFailedToTransaction: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case SEND_REVIEW_REQUEST:
      return { ...state, sendReviewInProgress: true, sendReviewError: null };
    case SEND_REVIEW_SUCCESS:
      return { ...state, sendReviewInProgress: false };
    case SEND_REVIEW_ERROR:
      return { ...state, sendReviewInProgress: false, sendReviewError: payload };

    case FETCH_MONTHLY_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_REQUEST: {
      const timeSlotsForDate = {
        ...removeOutdatedDateData(state.timeSlotsForDate),
        [payload]: {
          ...state.timeSlotsForDate[payload],
          fetchTimeSlotsError: null,
          fetchedAt: null,
          fetchTimeSlotsInProgress: true,
          timeSlots: [],
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_SUCCESS: {
      const dateId = payload.dateId;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchedAt: new Date().getTime(),
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_ERROR: {
      const dateId = payload.dateId;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, timeSlotsForDate };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const transitionInProgress = state => {
  return state.TransactionPage.transitionInProgress;
};

// ================ Action creators ================ //
export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });
const fetchTransactionSuccess = response => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchTransactionError = e => ({ type: FETCH_TRANSACTION_ERROR, error: true, payload: e });

const fetchTransitionsRequest = () => ({ type: FETCH_TRANSITIONS_REQUEST });
const fetchTransitionsSuccess = response => ({
  type: FETCH_TRANSITIONS_SUCCESS,
  payload: response,
});
const fetchTransitionsError = e => ({ type: FETCH_TRANSITIONS_ERROR, error: true, payload: e });

const transitionRequest = transitionName => ({ type: TRANSITION_REQUEST, payload: transitionName });
const captureDepositRequest = () => ({ type: CAPTURE_DEPOSIT_REQUEST });
const captureDepositSuccess = () => ({ type: CAPTURE_DEPOSIT_SUCCESS });
const captureDepositError = e => ({ type: CAPTURE_DEPOSIT_ERROR, error: true, payload: e });

const triggerDepositChargeRequest = () => ({ type: TRIGGER_DEPOSIT_CHARGE_REQUEST });
const triggerDepositChargeSuccess = () => ({ type: TRIGGER_DEPOSIT_CHARGE_SUCCESS });
const triggerDepositChargeError = e => ({ type: TRIGGER_DEPOSIT_CHARGE_ERROR, error: true, payload: e });

const requestTimeChangeRequest = () => ({ type: REQUEST_PICKUP_TIME_CHANGE });
const requestTimeChangeSuccess = () => ({ type: REQUEST_PICKUP_TIME_CHANGE_SUCCESS });
const requestTimeChangeError = e => ({ type: REQUEST_PICKUP_TIME_CHANGE_ERROR, error: true, payload: e });

const acceptTimeChangeRequest = () => ({ type: ACCEPT_PICKUP_TIME_CHANGE });
const acceptTimeChangeSuccess = () => ({ type: ACCEPT_PICKUP_TIME_CHANGE_SUCCESS });
const acceptTimeChangeError = e => ({ type: ACCEPT_PICKUP_TIME_CHANGE_ERROR, error: true, payload: e });

const declineTimeChangeRequest = () => ({ type: DECLINE_PICKUP_TIME_CHANGE });
const declineTimeChangeSuccess = () => ({ type: DECLINE_PICKUP_TIME_CHANGE_SUCCESS });
const declineTimeChangeError = e => ({ type: DECLINE_PICKUP_TIME_CHANGE_ERROR, error: true, payload: e });

const requestExtensionRequest = () => ({ type: REQUEST_EXTENSION });
const requestExtensionSuccess = () => ({ type: REQUEST_EXTENSION_SUCCESS });
const requestExtensionError = e => ({ type: REQUEST_EXTENSION_ERROR, error: true, payload: e });

const acceptExtensionRequest = () => ({ type: ACCEPT_EXTENSION });
const acceptExtensionSuccess = () => ({ type: ACCEPT_EXTENSION_SUCCESS });
const acceptExtensionError = e => ({ type: ACCEPT_EXTENSION_ERROR, error: true, payload: e });

const declineExtensionRequest = () => ({ type: DECLINE_EXTENSION });
const declineExtensionSuccess = () => ({ type: DECLINE_EXTENSION_SUCCESS });
const declineExtensionError = e => ({ type: DECLINE_EXTENSION_ERROR, error: true, payload: e });

const requestEarlyReturnRequest = () => ({ type: REQUEST_EARLY_RETURN });
const requestEarlyReturnSuccess = () => ({ type: REQUEST_EARLY_RETURN_SUCCESS });
const requestEarlyReturnError = e => ({ type: REQUEST_EARLY_RETURN_ERROR, error: true, payload: e });

const acceptEarlyReturnRequest = () => ({ type: ACCEPT_EARLY_RETURN });
const acceptEarlyReturnSuccess = () => ({ type: ACCEPT_EARLY_RETURN_SUCCESS });
const acceptEarlyReturnError = e => ({ type: ACCEPT_EARLY_RETURN_ERROR, error: true, payload: e });

const declineEarlyReturnRequest = () => ({ type: DECLINE_EARLY_RETURN });
const declineEarlyReturnSuccess = () => ({ type: DECLINE_EARLY_RETURN_SUCCESS });
const declineEarlyReturnError = e => ({ type: DECLINE_EARLY_RETURN_ERROR, error: true, payload: e });

const fileDamageClaimRequest = () => ({ type: FILE_DAMAGE_CLAIM });
const fileDamageClaimSuccess = () => ({ type: FILE_DAMAGE_CLAIM_SUCCESS });
const fileDamageClaimError = e => ({ type: FILE_DAMAGE_CLAIM_ERROR, error: true, payload: e });

const agreeDamageClaimRequest = () => ({ type: AGREE_DAMAGE_CLAIM });
const agreeDamageClaimSuccess = () => ({ type: AGREE_DAMAGE_CLAIM_SUCCESS });
const agreeDamageClaimError = e => ({ type: AGREE_DAMAGE_CLAIM_ERROR, error: true, payload: e });

const disputeDamageClaimRequest = () => ({ type: DISPUTE_DAMAGE_CLAIM });
const disputeDamageClaimSuccess = () => ({ type: DISPUTE_DAMAGE_CLAIM_SUCCESS });
const disputeDamageClaimError = e => ({ type: DISPUTE_DAMAGE_CLAIM_ERROR, error: true, payload: e });

const resolveDamageClaimForOwnerRequest = () => ({ type: RESOLVE_DAMAGE_CLAIM_FOR_OWNER });
const resolveDamageClaimForOwnerSuccess = () => ({ type: RESOLVE_DAMAGE_CLAIM_FOR_OWNER_SUCCESS });
const resolveDamageClaimForOwnerError = e => ({ type: RESOLVE_DAMAGE_CLAIM_FOR_OWNER_ERROR, error: true, payload: e });

const resolveDamageClaimForRenterRequest = () => ({ type: RESOLVE_DAMAGE_CLAIM_FOR_RENTER });
const resolveDamageClaimForRenterSuccess = () => ({ type: RESOLVE_DAMAGE_CLAIM_FOR_RENTER_SUCCESS });
const resolveDamageClaimForRenterError = e => ({ type: RESOLVE_DAMAGE_CLAIM_FOR_RENTER_ERROR, error: true, payload: e });

const resolveDamageClaimPlatformAbsorbsRequest = () => ({ type: RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS });
const resolveDamageClaimPlatformAbsorbsSuccess = () => ({ type: RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS_SUCCESS });
const resolveDamageClaimPlatformAbsorbsError = e => ({ type: RESOLVE_DAMAGE_CLAIM_PLATFORM_ABSORBS_ERROR, error: true, payload: e });

const transitionSuccess = () => ({ type: TRANSITION_SUCCESS });
const transitionError = e => ({ type: TRANSITION_ERROR, error: true, payload: e });

const cancelSaleRequest = () => ({ type: CANCEL_SALE_REQUEST });
const cancelSaleSuccess = () => ({ type: CANCEL_SALE_SUCCESS });
const cancelSaleError = e => ({ type: CANCEL_SALE_ERROR, error: true, payload: e });

const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = (messages, pagination) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
});
const fetchMessagesError = e => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: e });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });

const sendReviewRequest = () => ({ type: SEND_REVIEW_REQUEST });
const sendReviewSuccess = () => ({ type: SEND_REVIEW_SUCCESS });
const sendReviewError = e => ({ type: SEND_REVIEW_ERROR, error: true, payload: e });

export const fetchMonthlyTimeSlotsRequest = monthId => ({
  type: FETCH_MONTHLY_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchMonthlyTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchMonthlyTimeSlotsError = (monthId, error) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchTimeSlotsForDateRequest = dateId => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_REQUEST,
  payload: dateId,
});
export const fetchTimeSlotsForDateSuccess = (dateId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_SUCCESS,
  payload: { timeSlots, dateId },
});
export const fetchTimeSlotsForDateError = (dateId, error) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_ERROR,
  error: true,
  payload: { dateId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    dispatch(fetchTimeSlotsForDateRequest(dateId));
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(timeSlots => {
        dispatch(fetchTimeSlotsForDateSuccess(dateId, timeSlots));
        return timeSlots;
      })
      .catch(e => {
        dispatch(fetchTimeSlotsForDateError(dateId, storableError(e)));
        return [];
      });
  } else {
    const monthId = monthIdString(start, timeZone);
    dispatch(fetchMonthlyTimeSlotsRequest(monthId));
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(timeSlots => {
        dispatch(fetchMonthlyTimeSlotsSuccess(monthId, timeSlots));
        return timeSlots;
      })
      .catch(e => {
        dispatch(fetchMonthlyTimeSlotsError(monthId, storableError(e)));
        return [];
      });
  }
};

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// Helper to fetch correct image variants for different thunk calls
const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',

      // Listing images:
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const listingRelationship = txResponse => {
  return txResponse.data.data.relationships.listing.data;
};

export const fetchTransaction = (id, txRole, config) => (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest());

  return sdk.transactions
    .show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.currentStock',
          'listing.images',
          'listing.author',
          'listing.author.profileImage',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    )
    .then(response => {
      const listingId = listingRelationship(response).id;
      const entities = updatedEntities({}, response.data);
      const listingRef = { id: listingId, type: 'listing' };
      const transactionRef = { id, type: 'transaction' };
      const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
      const listing = denormalised[0];
      const transaction = denormalised[1];
      const processName = resolveLatestProcessName(transaction.attributes.processName);

      try {
        const process = getProcess(processName);
        const isInquiry = process.getState(transaction) === process.states.INQUIRY;

        // Fetch time slots for transactions that are in inquired state
        const canFetchTimeslots =
          txRole === 'customer' && isBookingProcess(processName) && isInquiry;

        if (canFetchTimeslots) {
          fetchMonthlyTimeSlots(dispatch, listing);
        }
      } catch (error) {
        console.log(`transaction process (${processName}) was not recognized`);
      }

      return response;
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchTransactionSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchTransactionError(storableError(e)));
      throw e;
    });
};

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
const refreshTx = (sdk, txId) => sdk.transactions.show({ id: txId }, { expand: true });
const refreshTransactionEntity = (sdk, txId, dispatch) => {
  delay(3000)
    .then(() => refreshTx(sdk, txId))
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const lastTransition = response?.data?.data?.attributes?.lastTransition;
      // We'll make another attempt if mark-received-from-purchased from default-purchase process is still the latest.
      if (lastTransition === 'transition/mark-received-from-purchased') {
        return delay(8000)
          .then(() => refreshTx(sdk, txId))
          .then(response => {
            dispatch(addMarketplaceEntities(response));
          });
      }
    })
    .catch(e => {
      // refresh failed, but we don't act upon it.
      console.log('error', e);
    });
};

export const makeTransition = (txId, transitionName, params) => (dispatch, getState, sdk) => {
  const state = getState();
  if (transitionInProgress(state)) {
    return Promise.reject(new Error('Transition already in progress'));
  }
  dispatch(transitionRequest(transitionName));

  const transaction = state?.marketplaceData?.entities?.transaction?.[txId?.uuid];
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  const process = getProcess(processName);

  // This calls the client app's server to make a privileged transition.
  const privilegedTransition = () =>
    transitionPrivileged({
      isSpeculative: false,
      orderData: params?.orderData || {},
      bodyParams: {
        id: txId,
        transition: transitionName,
        params: {}, // NOTE: lineItems and metadata are included on the server-side.
      },
      queryParams: {
        expand: true,
      },
    });
  const normalTransition = () =>
    sdk.transactions.transition({ id: txId, transition: transitionName, params }, { expand: true });
  const makeCall = process?.isPrivileged(transitionName) ? privilegedTransition : normalTransition;

  return makeCall()
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(transitionSuccess());
      dispatch(fetchCurrentUserNotifications());

      // There could be automatic transitions after this transition
      // For example mark-received-from-purchased > auto-complete.
      // Here, we make 1-2 delayed updates for the tx entity.
      // This way "leave a review" link should show up for the customer.
      refreshTransactionEntity(sdk, txId, dispatch);

      return response;
    })
    .catch(e => {
      dispatch(transitionError(storableError(e)));
      log.error(e, `${transitionName}-failed`, {
        txId,
        transition: transitionName,
      });
      throw e;
    });
};

const fetchMessages = (txId, page, config) => (dispatch, getState, sdk) => {
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };
  dispatch(fetchMessagesRequest());

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const totalMessages = getState().TransactionPage.totalMessages;

      // Original fetchMessages call succeeded
      dispatch(fetchMessagesSuccess(messages, pagination));

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1, config))
          .then(() => {
            // Original fetch was enough as a response for user action,
            // this just includes new incoming messages
          })
          .catch(() => {
            // Background update, no need to to do anything atm.
          });
      }
    })
    .catch(e => {
      dispatch(fetchMessagesError(storableError(e)));
      throw e;
    });
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState, sdk) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessages(txId, nextPage, config));
};

export const sendMessage = (txId, message, config) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then(response => {
      const messageId = response.data.data.id;

      // We fetch the first page again to add sent message to the page data
      // and update possible incoming messages too.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      return dispatch(fetchMessages(txId, 1, config))
        .then(() => {
          dispatch(sendMessageSuccess());
          return messageId;
        })
        .catch(() => dispatch(sendMessageSuccess()));
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has already sent a review, we need to make transition to
// transitions.REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendReviewAsSecond = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has not yet sent a review, we need to make transition to
// transitions.REVIEW_1_BY_<CUSTOMER/PROVIDER>
// However, the other party might have made the review after previous data synch point.
// So, error is likely to happen and then we must try another state transition
// by calling sendReviewAsSecond().
const sendReviewAsFirst = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      // If transaction transition is invalid, lets try another endpoint.
      if (isTransactionsTransitionInvalidTransition(e)) {
        return sendReviewAsSecond(id, params, role, dispatch, sdk);
      } else {
        dispatch(sendReviewError(storableError(e)));

        // Rethrow so the page can track whether the sending failed, and
        // keep the message in the form for a retry.
        throw e;
      }
    });
};

export const sendReview = (tx, transitionOptionsInfo, params, config) => (
  dispatch,
  getState,
  sdk
) => {
  const { reviewAsFirst, reviewAsSecond, hasOtherPartyReviewedFirst } = transitionOptionsInfo;
  dispatch(sendReviewRequest());

  return hasOtherPartyReviewedFirst
    ? sendReviewAsSecond(tx?.id, reviewAsSecond, params, dispatch, sdk, config)
    : sendReviewAsFirst(tx?.id, reviewAsFirst, params, dispatch, sdk, config);
};

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

export const fetchNextTransitions = id => (dispatch, getState, sdk) => {
  dispatch(fetchTransitionsRequest());

  return sdk.processTransitions
    .query({ transactionId: id })
    .then(res => {
      dispatch(fetchTransitionsSuccess(res.data.data));
    })
    .catch(e => {
      dispatch(fetchTransitionsError(storableError(e)));
    });
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
    });
};

// Helper to check if cancel is in progress
const cancelInProgress = getState => {
  return getState().TransactionPage.cancelInProgress;
};

// Customer cancellation. The server decides the refund tier (policy + 24h before the real
// pick-up time) and fires the operator cancel transition — see server/api/customer-cancel.js.
export const cancelSale = (id, config) => async (dispatch, getState, sdk) => {
  if (cancelInProgress(getState)) {
    return Promise.reject(new Error('Cancel already in progress'));
  }
  dispatch(cancelSaleRequest());

  try {
    const result = await post('/api/customer-cancel', { transactionId: id.uuid });
    if (result?.clientTransition) {
      // Legacy booking (older process version, customer-actor cancel): fire the transition the
      // server decided on.
      await sdk.transactions.transition({ id, transition: result.clientTransition, params: result.params });
    }
    await dispatch(fetchTransaction(id, 'customer', config));
    dispatch(cancelSaleSuccess());
    dispatch(fetchCurrentUserNotifications());
    return result;
  } catch (e) {
    dispatch(cancelSaleError(storableError(e)));
    log.error(e, 'cancel-sale-failed', { txId: id });
    throw e;
  }
};

/**
 * Capture security deposit authorization hold
 * @param {UUID} transactionId - Transaction ID
 * @param {Number} captureAmount - Amount to capture in cents (optional, defaults to full amount)
 * @param {String} reason - Reason for capturing deposit (required)
 */
export const captureDeposit = (transactionId, captureAmount, reason, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.captureDepositInProgress) {
    return Promise.reject(new Error('Capture already in progress'));
  }
  
  dispatch(captureDepositRequest());

  try {
    // Call API to capture deposit authorization
    const response = await post('/api/capture-deposit-authorization', {
      transactionId: transactionId.uuid,
      amount: captureAmount,
      reason
    });

    dispatch(captureDepositSuccess());
    
    // Refresh transaction to show updated deposit status
    dispatch(fetchTransaction(transactionId, 'provider', config));
    dispatch(fetchCurrentUserNotifications());
    
    return response;
  } catch (e) {
    dispatch(captureDepositError(storableError(e)));
    log.error(e, 'capture-deposit-failed', {
      txId: transactionId,
      captureAmount,
      reason
    });
    throw e;
  }
};

/**
 * Customer triggers deposit charge manually (from reminder-sent state)
 * Allows customer to pay deposit immediately instead of waiting for 24h auto-charge
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} config - Marketplace configuration
 */
export const triggerDepositCharge = (transactionId, config, preChargedData) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.triggerDepositChargeInProgress) {
    return Promise.reject(new Error('Deposit charge already in progress'));
  }

  dispatch(triggerDepositChargeRequest());

  try {
    // If a pre-charged paymentIntentId is provided (customer paid via the form first),
    // store it in protectedData so the worker knows to skip Stripe charging and just confirm state.
    const params = preChargedData?.piId
      ? {
          protectedData: {
            depositPaymentDetails: {
              stripePaymentIntentId: preChargedData.piId,
              status: 'succeeded',
              chargedVia: 'customer-form',
              amount: preChargedData.amount,
              currency: preChargedData.currency,
              chargedAt: new Date().toISOString(),
            },
          },
        }
      : {};

    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/customer-trigger-deposit-charge',
      params,
    });

    dispatch(triggerDepositChargeSuccess());
    
    // Refresh transaction to show updated state (should be waiting-charge-before-checkin)
    dispatch(fetchTransaction(transactionId, 'customer', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(triggerDepositChargeError(storableError(e)));
    log.error(e, 'trigger-deposit-charge-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Request a pickup time change
 * @param {UUID} transactionId - Transaction ID
 * @param {Date} newPickupTime - New requested pickup time
 * @param {String} reason - Optional reason for time change
 * @param {String} userRole - User's role ('customer' or 'provider')
 */
export const requestPickupTimeChange = (transactionId, newPickupTime, reason, userRole, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.requestTimeChangeInProgress) {
    return Promise.reject(new Error('Time change request already in progress'));
  }

  dispatch(requestTimeChangeRequest());

  try {
    // Get current transaction to determine current state
    const state = getState().TransactionPage;
    const transaction = state.transactionRef ? getState().marketplaceData.entities?.transaction?.[state.transactionRef.id.uuid] : null;
    
    if (!transaction) {
      throw new Error('Transaction not found');
    }

    // lastTransition is a string like "transition/accept", not an object
    // Derive state using the process state machine
    const processName = resolveLatestProcessName(transaction.attributes?.processName);
    const process = getProcess(processName);
    const currentState = process.getState(transaction);
    const role = userRole || 'customer';
    
    // Determine correct transition based on current state and user role
    let transitionName;
    
    if (role === 'customer') {
      if (currentState === 'accepted') {
        transitionName = 'transition/customer-request-pickup-time-change';
      } else if (currentState === 'reminder-sent') {
        transitionName = 'transition/customer-request-pickup-time-change-from-reminder';
      } else if (currentState === 'pre-paid') {
        transitionName = 'transition/customer-request-pickup-time-change-from-prepaid';
      } else if (currentState === 'waiting-charge-before-checkin') {
        transitionName = 'transition/customer-request-pickup-time-change-from-waiting';
      }
    } else if (role === 'provider') {
      if (currentState === 'accepted') {
        transitionName = 'transition/provider-request-pickup-time-change';
      } else if (currentState === 'reminder-sent') {
        transitionName = 'transition/provider-request-pickup-time-change-from-reminder';
      } else if (currentState === 'pre-paid') {
        transitionName = 'transition/provider-request-pickup-time-change-from-prepaid';
      } else if (currentState === 'waiting-charge-before-checkin') {
        transitionName = 'transition/provider-request-pickup-time-change-from-waiting';
      }
    }

    if (!transitionName) {
      throw new Error(`Cannot request time change from state: ${currentState}`);
    }

    const originalPickupTime = transaction.attributes?.booking?.attributes.start;

    // Handle both Date objects and ISO strings
    const newPickupTimeIso = typeof newPickupTime === 'string' 
      ? newPickupTime 
      : newPickupTime.toISOString();

    const protectedData = {
      pickupTimeChangeRequest: {
        requestedBy: role,
        requestedAt: new Date().toISOString(),
        originalPickupTime: originalPickupTime,
        newPickupTime: newPickupTimeIso,
        reason: reason || '',
        status: 'pending',
        respondedAt: null,
        responseNote: null
      }
    };

    // Call SDK transition with protected data; retry once on 401 (expired token)
    const doTransition = () => sdk.transactions.transition({
      id: transactionId,
      transition: transitionName,
      params: { protectedData }
    });

    try {
      await doTransition();
    } catch (transitionError) {
      if (transitionError?.status === 401) {
        // Token may have just expired — wait briefly and retry once
        await new Promise(resolve => setTimeout(resolve, 500));
        await doTransition();
      } else {
        throw transitionError;
      }
    }

    dispatch(requestTimeChangeSuccess());
    
    // Refresh transaction to show updated state
    dispatch(fetchTransaction(transactionId, role, config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(requestTimeChangeError(storableError(e)));
    log.error(e, 'request-pickup-time-change-failed', {
      txId: transactionId,
      newPickupTime
    });
    throw e;
  }
};

/**
 * Accept a pickup time change request
 * @param {UUID} transactionId - Transaction ID
 * @param {String} userRole - User's role ('customer' or 'provider')
 */
export const acceptPickupTimeChange = (transactionId, userRole, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.acceptTimeChangeInProgress) {
    return Promise.reject(new Error('Accept already in progress'));
  }

  dispatch(acceptTimeChangeRequest());

  try {
    const role = userRole || 'customer';
    const transitionName = role === 'customer'
      ? 'transition/accept-pickup-time-change'
      : 'transition/provider-accept-pickup-time-change';

    await sdk.transactions.transition({
      id: transactionId,
      transition: transitionName,
      params: { protectedData: {} }
    });

    dispatch(acceptTimeChangeSuccess());
    
    // Refresh transaction
    dispatch(fetchTransaction(transactionId, role, config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(acceptTimeChangeError(storableError(e)));
    log.error(e, 'accept-pickup-time-change-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Decline a pickup time change request
 * @param {UUID} transactionId - Transaction ID
 * @param {String} userRole - User's role ('customer' or 'provider')
 * @param {String} declineReason - Optional reason for declining
 */
export const declinePickupTimeChange = (transactionId, userRole, declineReason, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.declineTimeChangeInProgress) {
    return Promise.reject(new Error('Decline already in progress'));
  }

  dispatch(declineTimeChangeRequest());

  try {
    const role = userRole || 'customer';
    const transitionName = role === 'customer'
      ? 'transition/decline-pickup-time-change'
      : 'transition/provider-decline-pickup-time-change';

    // Read current request data to preserve fields (e.g. requestedBy, newPickupTime, reason)
    const txPageState = getState().TransactionPage;
    const txEntry = txPageState.transactionRef
      ? getState().marketplaceData.entities?.transaction?.[txPageState.transactionRef.id.uuid]
      : null;
    const currentRequest = txEntry?.attributes?.protectedData?.pickupTimeChangeRequest || {};

    const updatedRequest = {
      ...currentRequest,
      status: 'declined',
      declinedAt: new Date().toISOString(),
      ...(declineReason ? { declineReason } : {}),
    };

    await sdk.transactions.transition({
      id: transactionId,
      transition: transitionName,
      params: { protectedData: { pickupTimeChangeRequest: updatedRequest } }
    });

    dispatch(declineTimeChangeSuccess());
    
    // Refresh transaction
    dispatch(fetchTransaction(transactionId, role, config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(declineTimeChangeError(storableError(e)));
    log.error(e, 'decline-pickup-time-change-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Request an extension (customer during rental)
 * @param {UUID} transactionId - Transaction ID
 * @param {Date} newReturnTime - New proposed return date/time
 * @param {String} reason - Optional reason for extension
 * @param {String} originalReturnTime - Original return time from booking
 * @param {Object} config - Marketplace configuration
 */
export const requestExtension = (transactionId, requestData, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.requestExtensionInProgress) {
    return Promise.reject(new Error('Extension request already in progress'));
  }

  dispatch(requestExtensionRequest());

  try {
    const newReturnTimeIso = typeof requestData.newReturnTime === 'string' 
      ? requestData.newReturnTime 
      : requestData.newReturnTime.toISOString();

    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/customer-request-extension',
      params: { 
        protectedData: { 
          extensionRequest: {
            requestedBy: 'customer',
            requestedAt: new Date().toISOString(),
            originalReturnTime: requestData.originalReturnTime,
            newReturnTime: newReturnTimeIso,
            additionalDays: requestData.additionalDays || 0,
            additionalFee: requestData.additionalFee || 0,
            reason: requestData.reason || '',
            status: 'pending',
            respondedAt: null,
            responseNote: null
          }
        } 
      }
    });

    dispatch(requestExtensionSuccess());
    dispatch(fetchTransaction(transactionId, 'customer', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    console.error('[requestExtension] API Error:', {
      error: e,
      message: e.message,
      status: e.status,
      transactionId
    });
    
    dispatch(requestExtensionError(storableError(e)));
    log.error(e, 'request-extension-failed', {
      txId: transactionId,
      newReturnTime,
      apiErrors: e.data?.errors
    });
    throw e;
  }
};

/**
 * Accept an extension request (provider)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} config - Marketplace configuration
 */
export const acceptExtension = (transactionId, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.acceptExtensionInProgress) {
    return Promise.reject(new Error('Accept already in progress'));
  }

  dispatch(acceptExtensionRequest());

  try {
    // Get current transaction to access existing extensionRequest
    const txPageState = getState().TransactionPage;
    const currentTx = txPageState.transactionRef
      ? getState().marketplaceData.entities?.transaction?.[txPageState.transactionRef.id.uuid]
      : null;
    const currentExtensionRequest = currentTx?.attributes?.protectedData?.extensionRequest || {};
    
    // Recalculate additionalDays from dates to ensure correctness
    let calculatedAdditionalDays = currentExtensionRequest.additionalDays || 0;
    if (currentExtensionRequest.originalReturnTime && currentExtensionRequest.newReturnTime) {
      const originalDate = new Date(currentExtensionRequest.originalReturnTime);
      const newDate = new Date(currentExtensionRequest.newReturnTime);
      const timeDiff = newDate - originalDate;
      calculatedAdditionalDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    }
    
    // Recalculate additionalFee if needed
    let calculatedAdditionalFee = currentExtensionRequest.additionalFee || 0;
    if (calculatedAdditionalDays > 0 && (!currentExtensionRequest.additionalFee || currentExtensionRequest.additionalFee === 0)) {
      const listing = currentTx?.listing;
      const pricePerDay = listing?.attributes?.price?.amount || 0;
      const rentalFee = calculatedAdditionalDays * pricePerDay;
      const platformFee = Math.round(rentalFee * 0.15);
      calculatedAdditionalFee = rentalFee + platformFee;
    }
    
    const updatedExtensionRequest = {
      ...currentExtensionRequest,
      additionalDays: calculatedAdditionalDays,
      additionalFee: calculatedAdditionalFee,
      status: 'accepted',
      respondedAt: new Date().toISOString()
    };
    
    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/provider-accept-extension',
      params: { 
        protectedData: {
          extensionRequest: updatedExtensionRequest
        }
      }
    });

    dispatch(acceptExtensionSuccess());
    dispatch(fetchTransaction(transactionId, 'provider', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(acceptExtensionError(storableError(e)));
    log.error(e, 'accept-extension-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Decline an extension request (provider)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} config - Marketplace configuration
 */
export const declineExtension = (transactionId, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.declineExtensionInProgress) {
    return Promise.reject(new Error('Decline already in progress'));
  }

  dispatch(declineExtensionRequest());

  try {
    // Get current transaction to access existing extensionRequest
    const txPageState = getState().TransactionPage;
    const currentTx = txPageState.transactionRef
      ? getState().marketplaceData.entities?.transaction?.[txPageState.transactionRef.id.uuid]
      : null;
    const currentExtensionRequest = currentTx?.attributes?.protectedData?.extensionRequest || {};
    
    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/provider-decline-extension',
      params: { 
        protectedData: {
          extensionRequest: {
            ...currentExtensionRequest,
            status: 'declined',
            respondedAt: new Date().toISOString()
          }
        }
      }
    });

    dispatch(declineExtensionSuccess());
    dispatch(fetchTransaction(transactionId, 'provider', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(declineExtensionError(storableError(e)));
    log.error(e, 'decline-extension-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Request an early return (customer during rental)
 * @param {Date} newReturnTime - New proposed return date/time
 * @param {Boolean} noRefundAcknowledged - Customer acknowledges no refund
 */
export const requestEarlyReturn = (transactionId, newReturnTime, noRefundAcknowledged, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.requestEarlyReturnInProgress) {
    return Promise.reject(new Error('Early return request already in progress'));
  }

  dispatch(requestEarlyReturnRequest());

  try {
    const state = getState().TransactionPage;
    const transaction = state.transactionRef ? getState().marketplaceData.entities?.transaction?.[state.transactionRef.id.uuid] : null;
    
    if (!transaction) {
      throw new Error('Transaction not found');
    }

    const txProtectedData = transaction.attributes?.protectedData || {};
    const acceptedExtension = txProtectedData.extensionRequest?.status === 'accepted' && txProtectedData.extensionRequest?.newReturnTime;
    const originalReturnTime = acceptedExtension
      ? txProtectedData.extensionRequest.newReturnTime
      : transaction.booking?.attributes?.end;

    // Handle both Date objects and ISO strings
    const newReturnTimeIso = typeof newReturnTime === 'string' 
      ? newReturnTime 
      : newReturnTime.toISOString();

    const protectedData = {
      earlyReturnRequest: {
        requestedAt: new Date().toISOString(),
        originalReturnTime: originalReturnTime,
        newReturnTime: newReturnTimeIso,
        status: 'pending',
        respondedAt: null,
        noRefundAcknowledged: noRefundAcknowledged
      }
    };

    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/customer-request-early-return',
      params: { protectedData }
    });

    dispatch(requestEarlyReturnSuccess());
    dispatch(fetchTransaction(transactionId, 'customer', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(requestEarlyReturnError(storableError(e)));
    log.error(e, 'request-early-return-failed', {
      txId: transactionId,
      newReturnTime
    });
    throw e;
  }
};

/**
 * Accept an early return request (provider)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} config - Marketplace configuration
 */
export const acceptEarlyReturn = (transactionId, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.acceptEarlyReturnInProgress) {
    return Promise.reject(new Error('Accept already in progress'));
  }

  dispatch(acceptEarlyReturnRequest());

  try {
    const txPageState = getState().TransactionPage;
    const currentTx = txPageState.transactionRef
      ? getState().marketplaceData.entities?.transaction?.[txPageState.transactionRef.id.uuid]
      : null;
    const existingRequest = currentTx?.attributes?.protectedData?.earlyReturnRequest || {};

    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/provider-accept-early-return',
      params: {
        protectedData: {
          earlyReturnRequest: {
            ...existingRequest,
            status: 'accepted',
            respondedAt: new Date().toISOString(),
          },
        },
      },
    });

    dispatch(acceptEarlyReturnSuccess());
    dispatch(fetchTransaction(transactionId, 'provider', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(acceptEarlyReturnError(storableError(e)));
    log.error(e, 'accept-early-return-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Decline an early return request (provider)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} config - Marketplace configuration
 */
export const declineEarlyReturn = (transactionId, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.declineEarlyReturnInProgress) {
    return Promise.reject(new Error('Decline already in progress'));
  }

  dispatch(declineEarlyReturnRequest());

  try {
    const txPageState = getState().TransactionPage;
    const currentTx = txPageState.transactionRef
      ? getState().marketplaceData.entities?.transaction?.[txPageState.transactionRef.id.uuid]
      : null;
    const existingRequest = currentTx?.attributes?.protectedData?.earlyReturnRequest || {};

    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/provider-decline-early-return',
      params: {
        protectedData: {
          earlyReturnRequest: {
            ...existingRequest,
            status: 'declined',
            respondedAt: new Date().toISOString(),
          },
        },
      },
    });

    dispatch(declineEarlyReturnSuccess());
    dispatch(fetchTransaction(transactionId, 'provider', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(declineEarlyReturnError(storableError(e)));
    log.error(e, 'decline-early-return-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * File a damage claim (provider only)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} damageClaimData - Damage claim details (amount, type, description, photos)
 * @param {Object} config - Marketplace configuration
 */
export const fileDamageClaim = (transactionId, damageClaimData, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.fileDamageClaimInProgress) {
    return Promise.reject(new Error('File damage claim already in progress'));
  }

  dispatch(fileDamageClaimRequest());

  try {
    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/provider-file-damage-claim',
      params: { 
        protectedData: { 
          damageClaim: {
            ...damageClaimData,
            filedAt: new Date().toISOString(),
          }
        } 
      }
    });

    dispatch(fileDamageClaimSuccess());
    dispatch(fetchTransaction(transactionId, 'provider', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(fileDamageClaimError(storableError(e)));
    log.error(e, 'file-damage-claim-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Agree to a damage claim (customer)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} config - Marketplace configuration
 */
export const agreeDamageClaim = (transactionId, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.agreeDamageClaimInProgress) {
    return Promise.reject(new Error('Agree damage claim already in progress'));
  }

  dispatch(agreeDamageClaimRequest());

  try {
    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/customer-agree-damage-claim',
      params: { 
        protectedData: {
          damageClaimResponse: {
            renterAgreed: true,
            respondedAt: new Date().toISOString(),
          }
        }
      }
    });

    dispatch(agreeDamageClaimSuccess());
    dispatch(fetchTransaction(transactionId, 'customer', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(agreeDamageClaimError(storableError(e)));
    log.error(e, 'agree-damage-claim-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Dispute a damage claim with counter-evidence (customer)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} disputeData - Dispute reason and evidence photos
 * @param {Object} config - Marketplace configuration
 */
export const disputeDamageClaim = (transactionId, disputeData, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.disputeDamageClaimInProgress) {
    return Promise.reject(new Error('Dispute damage claim already in progress'));
  }

  dispatch(disputeDamageClaimRequest());

  try {
    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/customer-dispute-damage-claim',
      params: { 
        protectedData: {
          damageClaimResponse: {
            renterAgreed: false,
            respondedAt: new Date().toISOString(),
            ...disputeData,
          }
        }
      }
    });

    dispatch(disputeDamageClaimSuccess());
    dispatch(fetchTransaction(transactionId, 'customer', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(disputeDamageClaimError(storableError(e)));
    log.error(e, 'dispute-damage-claim-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Operator resolves damage claim in favor of owner (provider)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} resolutionData - Notes and decision data
 * @param {Object} config - Marketplace configuration
 */
export const resolveDamageClaimForOwner = (transactionId, resolutionData, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.resolveDamageClaimForOwnerInProgress) {
    return Promise.reject(new Error('Resolve claim already in progress'));
  }

  dispatch(resolveDamageClaimForOwnerRequest());

  try {
    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/operator-resolve-for-owner',
      params: { 
        protectedData: {
          damageClaimResolution: {
            resolvedAt: new Date().toISOString(),
            resolvedBy: 'operator',
            resolution: 'owner_wins',
            ...resolutionData,
          }
        }
      }
    });

    dispatch(resolveDamageClaimForOwnerSuccess());
    dispatch(fetchTransaction(transactionId, 'operator', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(resolveDamageClaimForOwnerError(storableError(e)));
    log.error(e, 'resolve-damage-claim-for-owner-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Operator resolves damage claim in favor of renter (customer)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} resolutionData - Notes and decision data
 * @param {Object} config - Marketplace configuration
 */
export const resolveDamageClaimForRenter = (transactionId, resolutionData, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.resolveDamageClaimForRenterInProgress) {
    return Promise.reject(new Error('Resolve claim already in progress'));
  }

  dispatch(resolveDamageClaimForRenterRequest());

  try {
    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/operator-resolve-for-renter',
      params: { 
        protectedData: {
          damageClaimResolution: {
            resolvedAt: new Date().toISOString(),
            resolvedBy: 'operator',
            resolution: 'renter_wins',
            ...resolutionData,
          }
        }
      }
    });

    dispatch(resolveDamageClaimForRenterSuccess());
    dispatch(fetchTransaction(transactionId, 'operator', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(resolveDamageClaimForRenterError(storableError(e)));
    log.error(e, 'resolve-damage-claim-for-renter-failed', {
      txId: transactionId
    });
    throw e;
  }
};

/**
 * Operator resolves damage claim with platform absorbing cost (for unclear cases < $50)
 * @param {UUID} transactionId - Transaction ID
 * @param {Object} resolutionData - Notes and decision data
 * @param {Object} config - Marketplace configuration
 */
export const resolveDamageClaimPlatformAbsorbs = (transactionId, resolutionData, config) => async (dispatch, getState, sdk) => {
  if (getState().TransactionPage.resolveDamageClaimPlatformAbsorbsInProgress) {
    return Promise.reject(new Error('Resolve claim already in progress'));
  }

  dispatch(resolveDamageClaimPlatformAbsorbsRequest());

  try {
    await sdk.transactions.transition({
      id: transactionId,
      transition: 'transition/operator-platform-absorbs',
      params: { 
        protectedData: {
          damageClaimResolution: {
            resolvedAt: new Date().toISOString(),
            resolvedBy: 'operator',
            resolution: 'platform_absorbs',
            ...resolutionData,
          }
        }
      }
    });

    dispatch(resolveDamageClaimPlatformAbsorbsSuccess());
    dispatch(fetchTransaction(transactionId, 'operator', config));
    dispatch(fetchCurrentUserNotifications());
    
    return true;
  } catch (e) {
    dispatch(resolveDamageClaimPlatformAbsorbsError(storableError(e)));
    log.error(e, 'resolve-damage-claim-platform-absorbs-failed', {
      txId: transactionId
    });
    throw e;
  }
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, config) => (dispatch, getState) => {
  const txId = new UUID(params.id);
  const state = getState().TransactionPage;
  const txRef = state.transactionRef;
  const txRole = params.transactionRole;

  // In case a transaction reference is found from a previous
  // data load -> clear the state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  // Sale / order (i.e. transaction entity in API)
  return Promise.all([
    dispatch(fetchTransaction(txId, txRole, config)),
    dispatch(fetchMessages(txId, 1, config)),
    dispatch(fetchNextTransitions(txId)),
    dispatch(fetchCurrentUser({ enforce: true, callParams: { include: ['stripeCustomer.defaultPaymentMethod', 'stripeCustomer', 'effectivePermissionSet', 'profileImage'] } })),
  ]);
};
